import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, Info, Globe, Twitter, MessageSquare, Copy, Check, RefreshCcw, 
  ExternalLink, Users, ArrowUp, ArrowDown, BarChart2, Zap, Calendar, 
  DollarSign, TrendingUp, TrendingDown, Clock, AlertTriangle, Share2, Wallet,
  Activity, Loader, X
} from 'lucide-react';
import { 
  getTokenDetailedStats, 
  getTokenInfo, 
  getTokenTransactions, 
  getTokenProfitAndLoss,
  getTokenData,
  extractTokenStats
} from '../../services/solanatrackerService';
import TokenSearchModal from '../../components/TokenSearchModal';
import { Token } from '../../types/supabase';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { wsService } from '../../services/websocketService';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserProfile } from '../../services/userProfileService';
import { getBalance } from '../../services/shyftService';
import { getTokenBalance } from '../../services/solanatrackerService';
import { SOL_MINT } from '../../services/jupiterService';
import { tradeEngine } from '../../services/tradeEngine';


// Extend types to fix type issues with API responses
declare module '../../services/solanatrackerService' {
  interface TokenDetailResponse {
    status?: 'success' | 'error';
    data?: any;
    error?: string;
  }
}

// Custom styles for the Token Info widget
const customStyles = `
  /* Global text styles for consistent font sizing */
  .token-info-widget .text-[8px] {
    font-size: 0.75rem !important; /* text-xs size */
  }
  
  .token-info-widget .text-[10px] {
    font-size: 0.75rem !important; /* text-xs size */
  }
  
  .token-info-widget h3.text-sm {
    font-size: 0.875rem !important; /* text-sm size */
  }
  
  .token-info-widget p, 
  .token-info-widget div, 
  .token-info-widget span,
  .token-info-widget button {
    font-size: 0.75rem !important; /* At least text-xs size */
  }

  .token-info-widget svg + span,
  .token-info-widget .text-xxs-exception {
    font-size: 0.7rem !important;
  }

  /* Widget specific styles */
  .token-info-widget {
    background-color: #16161a;
  }

  .token-info-widget .token-header {
    border-radius: 0.375rem 0.375rem 0 0;
    background: #16161a;
    border-bottom: 1px solid #24c35b;
  }
  
  .token-info-widget .section-header {
    background: #16161a;
    border-bottom: 1px solid #24c35b;
  }

  .token-info-widget .info-block {
    background: #16161a;
    border: 1px solid #24c35b;
    transition: all 0.15s ease;
  }
  
  .token-info-widget .info-block:hover {
    border-color: #24c35b;
    box-shadow: 0 0 8px rgba(36, 195, 91, 0.1);
  }
  
  .token-info-widget .search-btn {
    background-color: #16161a;
    border: 1px solid #24c35b;
    transition: all 0.15s ease;
  }
  
  .token-info-widget .search-btn:hover {
    border-color: #24c35b;
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  .token-info-widget .social-link {
    background: rgba(36, 195, 91, 0.1);
    color: #24c35b;
    transition: all 0.15s ease;
  }
  
  .token-info-widget .social-link:hover {
    background: rgba(36, 195, 91, 0.2);
  }
  
  /* Transaction items */
  .token-info-widget .transaction-item {
    border-bottom: 1px solid #24c35b;
    transition: background-color 0.15s ease;
  }
  
  .token-info-widget .transaction-item:hover {
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  .token-info-widget .transaction-buy {
    color: #24c35b;
  }
  
  .token-info-widget .transaction-sell {
    color: #ef4444;
  }
  
  /* Buttons */
  .token-info-widget .refresh-btn {
    background: #141418;
    border: 1px solid #24c35b;
    transition: all 0.15s ease;
  }
  
  .token-info-widget .refresh-btn:hover:not(:disabled) {
    border-color: #24c35b;
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  /* Value indicators */
  .token-info-widget .value-up {
    color: #24c35b;
  }
  
  .token-info-widget .value-down {
    color: #ef4444;
  }

  /* Input styles */
  .token-info-widget input::-webkit-outer-spin-button,
  .token-info-widget input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .token-info-widget input[type=number] {
    -moz-appearance: textfield;
  }
`;

// Define the props for the TokenInfo component
interface TokenInfoProps {
  selectedToken?: Token;
  onClose?: () => void;
  isWidget?: boolean;
}

// Types for SolanaTracker API responses
interface TokenPool {
  poolId: string;
  liquidity: {
    quote: number;
    usd: number;
  };
  price: {
    quote: number;
    usd: number;
  };
  tokenSupply: number;
  lpBurn: number;
  tokenAddress: string;
  marketCap: {
    quote: number;
    usd: number;
  };
  market: string;
  quoteToken: string;
  decimals: number;
  security: {
    freezeAuthority: string | null;
    mintAuthority: string | null;
  };
  lastUpdated: number;
  deployer: string;
  txns: {
    buys: number;
    total: number;
    volume: number;
    sells: number;
  };
}

interface TokenEvents {
  [key: string]: {
    priceChangePercentage: number;
  };
}

interface TokenRisk {
  rugged: boolean;
  risks: string[];
  score: number;
  jupiterVerified: boolean;
}

interface TokenResponse {
  token: {
    name: string;
    symbol: string;
    mint: string;
    uri: string;
    decimals: number;
    description: string;
    image: string;
    hasFileMetaData: boolean;
    strictSocials: Record<string, string>;
  };
  pools: TokenPool[];
  events: TokenEvents;
  risk: TokenRisk;
  buys: number;
  sells: number;
  txns: number;
  holders: number;
}

interface TokenUpdate {
  liquidity: {
    quote: number;
    usd: number;
  };
  price: {
    quote: number;
    usd: number;
  };
  tokenSupply: number;
  lpBurn: number;
  tokenAddress: string;
  marketCap: {
    quote: number;
    usd: number;
  };
  decimals: number;
  security: {
    freezeAuthority: string | null;
    mintAuthority: string | null;
  };
  quoteToken: string;
  market: string;
  deployer: string;
  lastUpdated: number;
  createdAt: number;
  poolId: string;
}

// Helper functions for formatting
function formatNumber(num: number | null | undefined, decimals = 2): string {
  if (num === undefined || num === null) return '0';
  
  if (num < 0.000001) {
    return num.toExponential(6);
  } else if (num < 0.01) {
    return num.toFixed(6);
  } else {
    return num.toFixed(decimals);
  }
}

function formatLargeNumber(num: number | null | undefined, decimals = 2): string {
  if (num === undefined || num === null) return '0';
  
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(decimals)}B`;
  } else if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(decimals)}M`;
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(decimals)}K`;
  } else {
    return num.toFixed(decimals);
  }
}

function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function formatShortAddress(address: string): string {
  if (!address) return '';
  return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString();
}

// Add TokenStatsSection component
const TokenStatsSection: React.FC<{
  token: Token | null;
  tokenStats: any | null;
  isLoading: boolean;
  onRefresh: (tokenAddress: string) => Promise<void>;
}> = ({ 
  token, 
  tokenStats, 
  isLoading, 
  onRefresh 
}) => {
  if (!token) return null;
  
  return (
    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-2"></div>
          <h3 className="text-black text-xs font-semibold">Token Statistics</h3>
        </div>
        <button 
          onClick={() => token.mint && onRefresh(token.mint)}
          className="text-[9px] text-[#24c35b] hover:text-[#20a855] flex items-center"
          disabled={isLoading}
        >
          <RefreshCcw size={9} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-3">
          <RefreshCcw size={14} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-600 text-[10px]">Loading token stats...</span>
        </div>
      ) : !tokenStats ? (
        <div className="text-gray-600 text-[10px] py-2">
          {token.mint ? 
            'Click refresh to load token statistics' : 
            'No token selected'}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="col-span-1">
            <div className="flex flex-col space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Price:</span>
                <span className="text-black text-[10px] font-mono">
                  ${tokenStats.price.toFixed(tokenStats.price < 0.01 ? 8 : 6)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Market Cap:</span>
                <span className="text-black text-[10px] font-mono">
                  ${formatLargeNumber(tokenStats.marketCap)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Liquidity:</span>
                <span className="text-black text-[10px] font-mono">
                  ${formatLargeNumber(tokenStats.liquidity)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="col-span-1">
            <div className="flex flex-col space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Total Txns:</span>
                <span className="text-black text-[10px] font-mono">
                  {formatLargeNumber(tokenStats.transactions.total)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Buys:</span>
                <span className="text-black text-[10px] font-mono text-[#24c35b]">
                  {formatLargeNumber(tokenStats.transactions.buys)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[10px]">Sells:</span>
                <span className="text-black text-[10px] font-mono text-red-500">
                  {formatLargeNumber(tokenStats.transactions.sells)}
                </span>
              </div>
            </div>
          </div>
          
          {tokenStats.lastUpdated && (
            <div className="col-span-2 mt-1 pt-1 border-t border-gray-300">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-[9px]">
                  Last updated: {new Date(tokenStats.lastUpdated).toLocaleTimeString()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Add OrderBook section component
const OrderBookSection: React.FC<{
  token: Token | null;
  isLoading: boolean;
  error: string | null;
}> = ({ token, isLoading, error }) => {
  const [trades, setTrades] = useState<any[]>([]);
  const [showSolValues, setShowSolValues] = useState<boolean>(false);
  const [wsStatus, setWsStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error' | 'disabled'>('disconnected');
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Function to fetch trades
  const fetchTrades = useCallback(async (tokenAddress: string) => {
    if (!tokenAddress) return;
    
    try {
      const response = await fetch(`https://data.solanatracker.io/trades/${tokenAddress}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SOLANA_TRACKER_API_KEY}`,
          'X-API-Key': import.meta.env.VITE_SOLANA_TRACKER_API_KEY
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch trades: ${response.status}`);
      }
      
      const data = await response.json();
      const sortedTrades = (data.trades || []).sort((a: any, b: any) => b.time - a.time);
      setTrades(sortedTrades);
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  }, []);

  // Setup WebSocket connection when token changes
  useEffect(() => {
    if (!token?.mint) return;
    
    const tokenAddress = token.mint;
    
    // Check connection status and update local state
    const updateConnectionStatus = () => {
      const status = wsService.getConnectionStatus();
      setWsStatus(status);
    };

    // Initially update status
    updateConnectionStatus();

    // First, always fetch initial data via REST API
    fetchTrades(tokenAddress);

    // Then attempt to set up the WebSocket connection for real-time updates
    wsService.ensureConnection().then((connected) => {
      if (connected) {
        updateConnectionStatus();
        
        // Clean up any previous subscription
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        
        // Subscribe to transactions for this token
        const handleTransaction = (data: any) => {
          const transactions = Array.isArray(data) ? data : [data];
          
          if (transactions.length > 0) {
            setTrades(prevTrades => {
              const newTrades = transactions
                .filter(tx => tx && (tx.tx || (tx.wallet && tx.time) || (tx.amount || tx.volume)))
                .map(tx => ({
                  tx: tx.tx || `unknown-${Date.now()}`,
                  amount: tx.amount || 0,
                  priceUsd: tx.priceUsd || tx.price || 0,
                  volume: tx.volume || 0,
                  volumeSol: tx.volumeSol,
                  type: tx.type || 'buy',
                  wallet: tx.wallet || 'unknown',
                  time: tx.time || Date.now(),
                  program: tx.program
                }));
              
              if (newTrades.length > 0) {
                const combinedTrades = [...newTrades, ...prevTrades];
                const uniqueTrades = combinedTrades
                  .filter((trade, index, self) => 
                    index === self.findIndex(t => t.tx === trade.tx)
                  );
                uniqueTrades.sort((a, b) => b.time - a.time);
                return uniqueTrades.slice(0, 100);
              }
              
              return prevTrades;
            });
          }
        };
        
        unsubscribeRef.current = wsService.subscribeToTransactions(tokenAddress, handleTransaction);
      } else {
        setWsStatus('disabled');
      }
    });
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      if (token?.mint) {
        wsService.leaveRoom(`transaction:${token.mint}`);
      }
    };
  }, [token?.mint, fetchTrades]);

  // Helper functions
  const getTimeSince = (timestamp: number): string => {
    const now = Date.now();
    const secondsAgo = Math.floor((now - timestamp) / 1000);
    
    if (secondsAgo < 60) return `${secondsAgo}s`;
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m`;
    if (secondsAgo < 86400) return `${Math.floor(secondsAgo / 3600)}h`;
    
    return `${Math.floor(secondsAgo / 86400)}d`;
  };

  const truncateAddress = (address: string): string => {
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };

  const calculateVolumeBarWidth = (volume: number): number => {
    const maxVolume = 1000;
    return Math.min(Math.max((volume / maxVolume) * 100, 5), 100);
  };

  // Render WebSocket status
  const renderWebSocketStatus = () => {
    if (wsStatus === 'connected') {
      return (
        <div className="flex items-center text-[9px] text-[#00fe76] gap-1 ml-1">
          <div className="w-1.5 h-1.5 bg-[#00fe76] rounded-full"></div>
          <span>Live</span>
        </div>
      );
    } else if (wsStatus === 'error' || wsStatus === 'disabled') {
      return (
        <div className="flex items-center text-[9px] text-gray-400 gap-1 ml-1">
          <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
          <span>REST</span>
        </div>
      );
    }
    return null;
  };

  // Render trades list
  const renderTradesList = () => {
    if (trades.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4">
          <p className="text-gray-400 text-[10px]">
            {token 
              ? `No trades found for ${token.symbol || 'this token'}`
              : 'Select a token to view trades'}
          </p>
        </div>
      );
    }

    // Show only the first 6 trades for the compact view
    const displayTrades = trades.slice(0, 6);

    return (
      <div className="overflow-x-auto hide-scrollbar">
        <table className="w-full text-xs">
          <thead className="bg-white border-b border-gray-200">
            <tr className="bg-white">
              <th className="px-1.5 py-1 text-left cursor-pointer bg-white" onClick={() => setShowSolValues(!showSolValues)}>
                <span className="text-[#24c35b] font-medium">Amount</span>
              </th>
              <th className="px-1.5 py-1 text-left bg-white">
                <span className="text-[#24c35b] font-medium">Trader</span>
              </th>
              <th className="px-1.5 py-1 text-right bg-white">
                <span className="text-[#24c35b] font-medium">Age</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-black">
            {displayTrades.map((trade, index) => (
              <tr 
                key={`${trade.tx}-${index}`} 
                className={`border-b border-gray-100 hover:bg-white ${trade.type === 'buy' ? 'hover:bg-green-50' : 'hover:bg-red-50'}`}
              >
                <td className="px-1.5 py-0.5 relative">
                  <div 
                    className={`absolute inset-y-0 left-0 ${trade.type === 'buy' ? 'volume-bar-buy' : 'volume-bar-sell'}`}
                    style={{ width: `${calculateVolumeBarWidth(trade.volume)}%` }}
                  ></div>
                  <div 
                    className={`relative z-10 flex items-center ${trade.type === 'buy' ? 'text-[#24c35b]' : 'text-red-500'} cursor-pointer`}
                    onClick={() => setShowSolValues(!showSolValues)}
                  >
                    {showSolValues ? (
                      <div className="flex items-center">
                        <img src="https://solana.com/src/img/branding/solanaLogoMark.svg" alt="Solana Logo" className="w-[8.4px] h-[8.4px] mr-1 -mt-[1px]" />
                        <span className="font-medium">{trade.volumeSol?.toFixed(4) || (trade.volume * 0.008).toFixed(4)}</span>
                      </div>
                    ) : (
                      <span className="font-medium">${Number(trade.volume).toFixed(2)}</span>
                    )}
                  </div>
                </td>
                <td className="px-1.5 py-0.5">
                  <div className="flex items-center">
                    <span className="text-xs text-black font-medium">{truncateAddress(trade.wallet)}</span>
                    <a
                      href={`https://solscan.io/account/${trade.wallet}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 text-[#24c35b] hover:text-[#20a855]"
                    >
                      <ExternalLink size={8} />
                    </a>
                  </div>
                </td>
                <td className="px-1.5 py-0.5 text-right">
                  <div className="flex items-center justify-end text-[10px] text-gray-600">
                    <span>{getTimeSince(trade.time)}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {trades.length > 6 && (
          <div className="text-center py-1 border-t border-gray-200">
            <span className="text-[10px] text-gray-600">
              Showing 6 of {trades.length} trades
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-2">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-2"></div>
          <h3 className="text-black text-xs font-semibold">Order Book</h3>
          {token && renderWebSocketStatus()}
        </div>
        <button 
          onClick={() => token?.mint && fetchTrades(token.mint)}
          disabled={isLoading || !token}
          className="p-0.5 rounded text-[#24c35b] hover:bg-[#24c35b]/10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh"
        >
          <RefreshCcw size={12} className={isLoading ? "animate-spin" : ""} />
        </button>
      </div>
      
      <div className="mt-1 max-h-[160px] overflow-y-auto custom-scrollbar">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-4">
            <Loader size={16} className="text-[#24c35b] animate-spin mb-1" />
            <p className="text-gray-600 text-[10px]">Loading...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-4">
            <p className="text-red-500 text-[10px] mb-1.5">{error}</p>
            <button 
              onClick={() => token?.mint && fetchTrades(token.mint)}
              className="px-1.5 py-0.5 bg-[#24c35b]/20 text-[#24c35b] text-[10px] rounded-md hover:bg-[#24c35b]/30"
            >
              Try Again
            </button>
          </div>
        ) : (
          renderTradesList()
        )}
      </div>
    </div>
  );
};

// Add QuickTradeSection component
const QuickTradeSection: React.FC<{
  token: Token | null;
  isLoading: boolean;
  error: string | null;
}> = ({ token, isLoading, error }) => {
  const walletUser = useWalletUser();
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState<string>('0.1');
  const [isLoadingBalances, setIsLoadingBalances] = useState<boolean>(false);
  const [solBalance, setSolBalance] = useState<string>('0');
  const [tokenBalance, setTokenBalance] = useState<string>('0');
  const [activeTradingWallet, setActiveTradingWallet] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [tradeSuccess, setTradeSuccess] = useState<string | null>(null);
  const [isBalancePending, setIsBalancePending] = useState<boolean>(false);

  // Get active trading wallet when component loads
  useEffect(() => {
    const fetchActiveTradingWallet = async () => {
      if (walletUser.walletAddress) {
        try {
          setIsLoadingBalances(true);
          const profile = await getUserProfile(walletUser.walletAddress);
          
          if (profile?.active_trade_wallet) {
            setActiveTradingWallet(profile.active_trade_wallet);
            
            // Fetch SOL balance
            const balance = await getBalance(profile.active_trade_wallet);
            setSolBalance((balance / 1_000_000_000).toFixed(5));
            
            // Fetch token balance if token is selected
            if (token?.mint) {
              const tokBalance = await getTokenBalance(profile.active_trade_wallet, token.mint);
              setTokenBalance(tokBalance);
            }
          }
        } catch (err) {
          console.error('Error fetching active trading wallet:', err);
          setTradeError('Error connecting to trading wallet');
        } finally {
          setIsLoadingBalances(false);
        }
      }
    };

    fetchActiveTradingWallet();
  }, [walletUser.walletAddress, token?.mint]);

  // Add a function to fetch both SOL and token balances
  const fetchBalances = async (forceRefresh = false) => {
    try {
      if (!activeTradingWallet) return;
      
      // Fetch SOL balance
      const newSolBalance = await getBalance(activeTradingWallet);
      const solBalanceFormatted = (newSolBalance / 1_000_000_000).toFixed(5);
      setSolBalance(solBalanceFormatted);
      
      // Fetch token balance if we have a token
      if (token?.mint) {
        const newTokenBalance = await getTokenBalance(activeTradingWallet, token.mint);
        setTokenBalance(newTokenBalance);
        
        // If token balance is zero after a sell, reset amount
        if (tradeMode === 'sell' && parseFloat(newTokenBalance) === 0) {
          setAmount('1');
        }
      }
      
      setIsBalancePending(false);
    } catch (err) {
      console.error('Error fetching balances:', err);
    }
  };

  // Handle trade execution
  const executeTrade = async () => {
    if (!walletUser.walletAddress || !activeTradingWallet || !token?.mint) {
      setTradeError('Please connect your wallet and select a token');
      return;
    }

    // Validate amount
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setTradeError('Please enter a valid amount');
      return;
    }

    // Check balance for the trade
    if (tradeMode === 'buy') {
      // Check if there's enough SOL 
      if (amountNum > parseFloat(solBalance)) {
        setTradeError(`Insufficient SOL balance. You have ${solBalance} SOL.`);
        return;
      }
    } else {
      // Don't allow trades if the balance is pending update
      if (isBalancePending) {
        setTradeError('Balance update pending. Please wait a moment and try again.');
        return;
      }
      
      // Check if there's enough token balance
      if (amountNum > parseFloat(tokenBalance)) {
        setTradeError(`Insufficient ${token.symbol} balance. You have ${tokenBalance} ${token.symbol}.`);
        return;
      }
    }

    try {
      setIsExecuting(true);
      setTradeError(null);
      setTradeSuccess(null);

      let result: any;

      if (tradeMode === 'sell') {
        // 🔥 **CONSISTENCY FIX**: Use the same method as TradesBuySell for selling
        console.log(`Executing sell using jupiterService: ${amount} ${token.symbol}`);
        
        // If this is a sell, immediately mark balance as pending update to prevent double-sells
        setIsBalancePending(true);
        
        // Also update UI immediately to prevent visual double-sells
        const optimisticBalance = Math.max(0, parseFloat(tokenBalance) - amountNum).toString();
        setTokenBalance(optimisticBalance);
        
        // Import jupiterService
        const { jupiterService } = await import('../../services/jupiterService');
        
        // Use the same method as TradesBuySell
        result = await jupiterService.sellToken({
          tokenMint: token.mint,
          amount: amountNum,
          slippage: parseFloat('20') // Using default slippage like original code
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Sell transaction failed');
        }
        
        // Set success message using the transaction ID from jupiterService result
        setTradeSuccess(`Sell transaction executed successfully!`);
        
      } else {
        // For buy trades, continue using the existing tradeEngine method (same as TradesBuySell)
        console.log(`Executing buy trade: ${amount} SOL for ${token.symbol}`);

      const tradeParams = {
          inputMint: SOL_MINT,
          outputMint: token.mint,
        amount: amount, // Use original string amount to preserve precision
        userWalletAddress: walletUser.walletAddress,
        tradingWalletAddress: activeTradingWallet,
        slippagePercent: '20' // Using default slippage
      };

        result = await tradeEngine.executeSwap(tradeParams);
        setTradeSuccess(`Buy transaction executed successfully!`);
      }

      // Immediately force wallet refresh events
      window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
        detail: {
          walletPublicKey: activeTradingWallet
        }
      }));

      // After a successful trade, poll for balance updates
      const pollBalanceUpdates = async (attempts = 30, interval = 200) => {
        console.log(`Starting balance polling for ${attempts} attempts every ${interval}ms`);
        
        // Listen for direct token refresh events during the polling period
        const handleTokenRefresh = (event: Event) => {
          const customEvent = event as CustomEvent<{address: string, tokenMint?: string, timestamp: number}>;
          
          if (customEvent.detail.address === activeTradingWallet) {
            console.log('🔄 Received token refresh event, updating balances immediately');
            // If a specific token refresh matches our token, update immediately
            if (!customEvent.detail.tokenMint || customEvent.detail.tokenMint === token?.mint) {
              // Force reload balances right away
              fetchBalances(true);
            }
          }
        };
        
        // Add event listener for token refresh
        window.addEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
        
        let attempt = 0;
        const pollInterval = setInterval(async () => {
          attempt++;
          console.log(`🔄 Balance poll attempt ${attempt}/${attempts}`);
          
          // First try the direct API method
          try {
            const freshBalance = await getTokenBalance(activeTradingWallet || '', token?.mint || '', true);
            console.log(`📈 Poll attempt ${attempt} - fresh balance: ${freshBalance}`);
            
            // Update UI with any result found
            if (freshBalance && freshBalance !== '0') {
              console.log(`✅ Valid balance found in poll ${attempt}: ${freshBalance}`);
              setTokenBalance(freshBalance);
              setIsBalancePending(false);
              
              // If we found a valid balance, we can stop polling
              if (attempt >= 3) { // Let at least 3 attempts happen to ensure stability
                clearInterval(pollInterval);
                window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
                console.log('✅ Balance polling completed early - valid balance found');
                return;
              }
            }
          } catch (err) {
            console.error(`Error during poll attempt ${attempt}:`, err);
          }
          
          // Also try alternative methods every few attempts
          if (attempt % 3 === 0) {
            try {
              // Try refreshWalletTokens again
              const { refreshWalletTokens } = await import('../../services/solanatrackerService');
              const result = await refreshWalletTokens(activeTradingWallet || '', token?.mint, true);
              
              if (result.status === 'success' && result.data?.tokens?.length > 0) {
                const refreshedToken = result.data.tokens[0];
                console.log(`📊 Retry refresh successful: Balance = ${refreshedToken.balance}`);
                
                if (refreshedToken.balance && refreshedToken.balance !== '0') {
                  setTokenBalance(refreshedToken.balance);
                  setIsBalancePending(false);
                }
              }
            } catch (err) {
              console.error(`Error during retry refresh at attempt ${attempt}:`, err);
            }
          }
          
          // As a fallback, also try the fetchBalances method
          await fetchBalances(true);
          
          if (attempt >= attempts) {
            clearInterval(pollInterval);
            window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
            console.log('Balance polling completed');
            setIsBalancePending(false); // Make sure we clear the pending flag at the end
          }
        }, interval);
        
        // Clear polling if component unmounts during this process
        return () => {
          clearInterval(pollInterval);
          window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
        };
      };
      
      // Start polling for balance updates
      pollBalanceUpdates();

      // Dispatch a global trade completed event that other components can listen for
      window.dispatchEvent(new CustomEvent('ekko:trade_completed', {
        detail: {
          type: tradeMode,
          token: token,
          amount: amount,
          walletAddress: activeTradingWallet,
          timestamp: Date.now()
        }
      }));

    } catch (error) {
      console.error('Error executing trade:', error);
      
      // Reset balance pending flag
      setIsBalancePending(false);
      
      // If sell failed, restore original balance in UI
      if (tradeMode === 'sell' && token?.mint) {
        // Recheck balance to ensure UI is correct
        fetchBalances(true);
      }
      
      // Handle different types of errors
      if (error instanceof Error) {
        // Handle specific error messages 
        if (error.message.includes("Insufficient")) {
          setTradeError(`Insufficient balance: ${error.message}`);
        } else if (error.message.includes("slippage")) {
          setTradeError(`Slippage error: ${error.message}. Try increasing slippage tolerance.`);
        } else if (error.message.includes("Token not found")) {
          setTradeError(`This token is too new or not supported by Jupiter yet.`);
        } else if (error.message.includes("TOKEN_NOT_TRADABLE")) {
          setTradeError(`This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.`);
        } else {
          setTradeError(`Failed to execute trade: ${error.message}`);
        }
      } else {
        setTradeError('An unknown error occurred');
      }
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="mt-2">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-2"></div>
          <h3 className="text-black text-xs font-semibold">Quick Trade</h3>
        </div>
        <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
          <button
            onClick={() => setTradeMode('buy')}
            className={`px-2 py-0.5 text-[10px] rounded ${
              tradeMode === 'buy' ? 'bg-[#24c35b] text-white shadow-sm' : 'text-gray-600 hover:text-black'
            }`}
          >
            Buy
          </button>
          <button
            onClick={() => setTradeMode('sell')}
            className={`px-2 py-0.5 text-[10px] rounded ${
              tradeMode === 'sell' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-600 hover:text-black'
            }`}
          >
            Sell
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg p-2 border border-gray-200">
        <div className="flex justify-between text-[10px] text-gray-600 mb-1">
          <span>Balance:</span>
          <span className="text-black font-medium">{tradeMode === 'buy' ? `${solBalance} SOL` : `${tokenBalance} ${token?.symbol || ''}`}</span>
        </div>

        <div className="relative">
          <input
            type="text"
            value={amount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setAmount(value);
              }
            }}
            className="w-full bg-white text-black text-[10px] py-1 px-2 rounded border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b] focus:outline-none"
            placeholder={`Enter ${tradeMode === 'buy' ? 'SOL' : 'token'} amount`}
            inputMode="decimal"
          />
          {tradeMode === 'sell' && (
            <button
              onClick={() => setAmount(tokenBalance)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[10px] text-[#24c35b] hover:text-[#1eaa4e] font-medium"
            >
              MAX
            </button>
          )}
        </div>

        <button
          onClick={executeTrade}
          disabled={isExecuting || !token || !activeTradingWallet || !amount || parseFloat(amount) <= 0}
          className="w-full mt-2 bg-[#24c35b] text-white text-[10px] py-1 rounded hover:bg-[#1eaa4e] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {isExecuting ? (
            <span className="flex items-center justify-center">
              <Loader size={12} className="animate-spin mr-1" />
              {tradeMode === 'buy' ? 'Buying...' : 'Selling...'}
            </span>
          ) : (
            `${tradeMode === 'buy' ? 'Buy' : 'Sell'} ${token?.symbol || 'Token'}`
          )}
        </button>

        {tradeError && (
          <div className="mt-2 text-[10px] text-red-500">
            {tradeError}
          </div>
        )}

        {tradeSuccess && (
          <div className="mt-2 text-[10px] text-[#24c35b]">
            {tradeSuccess}
          </div>
        )}
      </div>
    </div>
  );
};

// Main component
const TokenInfo: React.FC<TokenInfoProps> = ({
  selectedToken,
  onClose,
  isWidget = false
}) => {
  // State for token and token data
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Search modal state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  
  // State for copied addresses
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Use the local token state if available, otherwise use the prop
  const effectiveToken = localSelectedToken || selectedToken;
  
  // Update local state when prop changes
  useEffect(() => {
    if (selectedToken) {
      setLocalSelectedToken(selectedToken);
    }
  }, [selectedToken]);
  
  // Listen for token selection events from the app
  useEffect(() => {
    // Handler for token selection events
    const handleTokenSelected = (event: CustomEvent<{ token: Token; force_refresh?: boolean }>) => {
      const { token } = event.detail;
      console.log('TokenInfo received token selection event:', token);
      console.log(`Token details - mint: ${token.mint}, symbol: ${token.symbol || ''}, name: ${token.name || ''}`);
      setLocalSelectedToken(token);
    };

    // Add event listeners for various token update events
    window.addEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    window.addEventListener('ekko:token_updated', handleTokenSelected as EventListener);
    window.addEventListener('ekko:canvas_update_widgets', handleTokenSelected as EventListener);
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('ekko:token_selected', handleTokenSelected as EventListener);
      window.removeEventListener('ekko:token_updated', handleTokenSelected as EventListener);
      window.removeEventListener('ekko:canvas_update_widgets', handleTokenSelected as EventListener);
    };
  }, []);
  
  // Fetch token data when token is available
  useEffect(() => {
    if (effectiveToken?.mint) {
      console.log(`TokenInfo: Token mint detected, initiating data fetch for: ${effectiveToken.mint}`);
      fetchTokenData();
    } else {
      console.log('TokenInfo: No token mint available, skipping data fetch');
      // Make sure we're not in loading state when there's no token
      setIsLoading(false);
    }
  }, [effectiveToken?.mint]);
  
  // Update the fetchTokenData function
  const fetchTokenData = async (silentRefresh = false) => {
    if (!effectiveToken?.mint) {
      console.log('TokenInfo: fetchTokenData called but no token mint available');
      return;
    }
    
    console.log(`TokenInfo: Fetching data for token ${effectiveToken.symbol} (${effectiveToken.mint})`);
    
    if (!silentRefresh) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    
    setError(null);
    
    try {
      // First get the token info
      const response = await getTokenInfo(effectiveToken.mint);
      
      if (response.status === 'success' && response.data) {
        setTokenData(response.data);
        console.log(`TokenInfo: Successfully loaded data for ${effectiveToken.symbol}`);
        
        // Then get the token stats
        const statsResponse = await getTokenData(effectiveToken.mint);
        if (statsResponse.status === 'success' && statsResponse.data) {
          const stats = extractTokenStats(statsResponse);
          if (stats) {
            setTokenStats(stats);
            console.log("Fetched token stats:", stats);
          } else {
            console.warn("Failed to extract token stats from response:", statsResponse);
          }
        } else {
          console.warn("Failed to fetch token stats:", statsResponse.error);
        }
      } else {
        if (!silentRefresh) {
          setError(response.error || 'Failed to fetch token data');
          console.error(`TokenInfo: Failed to load data for ${effectiveToken.symbol}:`, response.error);
        }
      }
    } catch (err) {
      console.error('Error fetching token data:', err);
      if (!silentRefresh) {
        setError(err instanceof Error ? err.message : 'Failed to fetch token data');
      }
    } finally {
      if (!silentRefresh) {
        setIsLoading(false);
      }
      setIsRefreshing(false);
    }
  };
  
  // Subscribe to WebSocket updates
  useEffect(() => {
    if (effectiveToken?.mint) {
      // Join the token room
      wsService.joinRoom(`token:${effectiveToken.mint}`);
      
      // Listen for token updates
      const handleTokenUpdate = (data: TokenUpdate) => {
        console.log(`Token ${effectiveToken.mint} update:`, data);
        
        // Update token data with new information
        setTokenData((prevData: TokenResponse | null) => {
          if (!prevData) return null;
          
          // Find the pool that matches the updated poolId
          const updatedPools = prevData.pools.map(pool => {
            if (pool.poolId === data.poolId) {
              return {
                ...pool,
                liquidity: data.liquidity,
                price: data.price,
                tokenSupply: data.tokenSupply,
                lpBurn: data.lpBurn,
                marketCap: data.marketCap,
                lastUpdated: data.lastUpdated
              };
            }
            return pool;
          });
          
          return {
            ...prevData,
            pools: updatedPools
          };
        });
      };
      
      wsService.on(`token:${effectiveToken.mint}`, handleTokenUpdate);
      
      // Cleanup subscription
      return () => {
        wsService.leaveRoom(`token:${effectiveToken.mint}`);
        // The WebSocket service will automatically clean up event listeners when leaving a room
      };
    }
  }, [effectiveToken?.mint]);

  // Fetch initial data when token changes
  useEffect(() => {
    if (effectiveToken?.mint) {
      fetchTokenData();
    }
  }, [effectiveToken?.mint]);
  
  // Handle refresh button click
  const handleRefresh = () => {
    if (!effectiveToken?.mint) return;
    
    fetchTokenData();
  };
  
  // Handle copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };
  
  // Add token stats state
  const [tokenStats, setTokenStats] = useState<any | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);

  // Add auto-refresh interval reference
  const refreshIntervalRef = useRef<number | null>(null);

  // Add fetchTokenStats function
  const fetchTokenStats = useCallback(async (tokenAddress: string) => {
    if (!tokenAddress) return;
    
    setIsLoadingStats(true);
    try {
      const response = await getTokenData(tokenAddress);
      const stats = extractTokenStats(response);
      setTokenStats(stats);
      
      console.log("Fetched token stats:", stats);
    } catch (error) {
      console.error("Error fetching token stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  // Set up auto-refresh of token statistics
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    
    // If no token is selected, don't set up the interval
    if (!effectiveToken?.mint) {
      return;
    }
    
    // Set up a new interval to refresh token stats every 1 second
    refreshIntervalRef.current = window.setInterval(() => {
      const tokenMint = effectiveToken?.mint;
      if (tokenMint) {
        // Skip setting loading state to true for auto-refresh to avoid UI flicker
        setIsLoadingStats(false);
        getTokenData(tokenMint)
          .then((response) => {
            const stats = extractTokenStats(response);
            if (stats) {
              setTokenStats(stats);
              console.log("Token stats refreshed:", stats);
            }
          })
          .catch((error) => {
            console.error("Error auto-refreshing token stats:", error);
          });
      }
    }, 1000);
    
    // Clean up interval when component unmounts or token changes
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [effectiveToken?.mint]);

  // Update the handleTokenSelection function
  const handleTokenSelection = (token: Token) => {
    setIsSearchModalOpen(false);
    setLocalSelectedToken(token);
    
    // Fetch token data when a new token is selected
    if (token.mint) {
      fetchTokenData();
    }
  };

  // Render token info content
  const renderTokenInfo = () => {
    if (!effectiveToken) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-center">
            <p className="text-gray-600 text-sm mb-2">No token selected</p>
            <button 
              ref={searchButtonRef}
              onClick={() => setIsSearchModalOpen(true)}
              className="px-4 py-2 rounded-lg bg-[#24c35b] text-white flex items-center hover:bg-[#20a855] transition-all shadow-sm"
            >
              <Search size={14} className="mr-1" /> 
              Search for a token
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        {/* Token Header Section */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 mb-3">
          <div className="flex items-center">
            {effectiveToken.image_url ? (
              <img 
                src={effectiveToken.image_url} 
                alt={effectiveToken.name || effectiveToken.symbol || 'Token'} 
                className="w-8 h-8 rounded-full mr-3" 
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://solana.com/src/img/branding/solanaLogoMark.svg';
                }}
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-200 mr-3 flex items-center justify-center">
                <span className="text-sm text-gray-600 font-medium">{effectiveToken.symbol?.charAt(0) || '?'}</span>
              </div>
            )}
            <div>
              <h3 className="text-sm font-bold text-black">
                {effectiveToken.symbol || 'Unknown Token'}
              </h3>
              <p className="text-xs text-gray-600">
                {effectiveToken.name || 'No name available'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => copyToClipboard(effectiveToken.mint)}
              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {copiedAddress === effectiveToken.mint ? (
                <Check size={14} className="text-[#24c35b]" />
              ) : (
                <Copy size={14} className="text-gray-500" />
              )}
            </button>
            <button 
              onClick={handleRefresh}
              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isLoading || isRefreshing}
            >
              <RefreshCcw size={14} className={`text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="p-3">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader size={24} className="text-[#24c35b] animate-spin mb-3" />
              <p className="text-gray-400 text-[10px]">Loading token information...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
                <AlertTriangle size={20} className="text-red-600 mb-2 mx-auto" />
                <div className="text-red-600 text-sm font-medium mb-1">Error Loading Token</div>
                <p className="text-red-500 text-xs mb-3">{error}</p>
                <button 
                  onClick={handleRefresh}
                  className="px-3 py-1.5 bg-[#24c35b] text-white text-xs rounded-lg hover:bg-[#20a855] transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Token Address Row */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-gray-400">Token Address:</span>
                  <a 
                    href={`https://solscan.io/token/${effectiveToken.mint}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-[#24c35b] hover:underline flex items-center"
                  >
                    View on Solscan
                    <ExternalLink size={9} className="inline-block ml-1" />
                  </a>
                </div>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 px-2 py-1 rounded-md">
                  <span className="text-[10px] text-black font-mono truncate">
                    {formatShortAddress(effectiveToken.mint)}
                  </span>
                  <button 
                    onClick={() => copyToClipboard(effectiveToken.mint)}
                    className="p-1 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {copiedAddress === effectiveToken.mint ? (
                      <Check size={12} className="text-[#24c35b]" />
                    ) : (
                      <Copy size={12} className="text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Social Links */}
              {(effectiveToken.twitter || effectiveToken.website || effectiveToken.telegram) && (
                <div className="mb-3">
                  <div className="mb-1">
                    <span className="text-[10px] text-gray-400">Social Links:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {effectiveToken.website && (
                      <a 
                        href={effectiveToken.website.startsWith('http') ? effectiveToken.website : `https://${effectiveToken.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link p-1.5 rounded-md"
                      >
                        <Globe size={14} />
                      </a>
                    )}
                    {effectiveToken.twitter && (
                      <a 
                        href={effectiveToken.twitter.startsWith('http') ? effectiveToken.twitter : `https://twitter.com/${effectiveToken.twitter.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link p-1.5 rounded-md"
                      >
                        <Twitter size={14} />
                      </a>
                    )}
                    {effectiveToken.telegram && (
                      <a 
                        href={effectiveToken.telegram.startsWith('http') ? effectiveToken.telegram : `https://t.me/${effectiveToken.telegram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link p-1.5 rounded-md"
                      >
                        <MessageSquare size={14} />
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Token Statistics */}
              <TokenStatsSection 
                token={effectiveToken}
                tokenStats={tokenStats}
                isLoading={isLoadingStats}
                onRefresh={fetchTokenStats}
              />
              
              {/* Order Book */}
              <OrderBookSection 
                token={effectiveToken}
                isLoading={isLoading}
                error={error}
              />
              
              {/* Quick Trade */}
              <QuickTradeSection 
                token={effectiveToken}
                isLoading={isLoading}
                error={error}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // Add proper cleanup for WebSocket connections
  useEffect(() => {
    return () => {
      // Clean up all WebSocket subscriptions and connections
      if (effectiveToken?.mint) {
        // Leave the token room
        wsService.leaveRoom(`token:${effectiveToken.mint}`);
        
        // Leave the transaction room
        wsService.leaveRoom(`transaction:${effectiveToken.mint}`);
        
        // Leave the price room
        wsService.leaveRoom(`price:${effectiveToken.mint}`);
      }
    };
  }, [effectiveToken?.mint]);

  // Add scrollable container style for consistency with TokenStats
  const scrollableContainerStyle = `
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
      overflow-y: auto;
      max-height: 100%;
      height: 100%;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
  `;

  const content = (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="token-info">
      <style>{globalWidgetTextStyles}</style>
      <style>{scrollableContainerStyle}</style>
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Info size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Token Info</h2>
        </div>
        <div className="flex items-center gap-2">
          {effectiveToken && (
            <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">{effectiveToken.name} ({effectiveToken.symbol})</span>
          )}
          
          {/* Always show search button */}
          <button 
            ref={searchButtonRef}
            onClick={() => setIsSearchModalOpen(true)}
            className="p-2 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 text-[#24c35b] transition-colors flex items-center border border-[#24c35b]/20"
          >
            <Search size={14} className="mr-1" />
            <span className="text-xs">{effectiveToken ? 'Change' : 'Search Token'}</span>
          </button>
          
          {/* Show refresh button only when a token is selected */}
          {effectiveToken && (
            <button 
              onClick={handleRefresh} 
              className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-black transition-colors"
              disabled={isLoading || isRefreshing}
            >
              <RefreshCcw size={14} className={isLoading || isRefreshing ? "animate-spin" : ""} />
            </button>
          )}
          
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-grow overflow-y-auto px-3 py-3 custom-scrollbar bg-white">
        {renderTokenInfo()}
      </div>
      
      {/* Token Search Modal */}
      {isSearchModalOpen && (
        <TokenSearchModal 
          isOpen={isSearchModalOpen} 
          onClose={() => setIsSearchModalOpen(false)} 
          onSelectToken={handleTokenSelection}
          referenceElement={searchButtonRef.current}
        />
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Info size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Token Info</h2>
        </div>
        <div className="flex items-center gap-2">
          {!effectiveToken && (
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="rounded bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] transition-colors px-2 py-1 text-xs flex items-center"
            >
              <Search size={12} className="mr-1" />
              Search Token
            </button>
          )}
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              &times;
            </button>
          )}
        </div>
      </div>
      
      {content}
    </div>
  );
};

export default TokenInfo;

/**
 * Factory function to create a TokenInfo widget with specified props
 * @param props - The properties to initialize the TokenInfo component with
 * @returns A TokenInfo widget object with content and metadata
 */
export function createTokenInfoWidget(props?: TokenInfoProps) {
  const uniqueKey = `token-info-${props?.selectedToken?.mint || 'default'}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  return {
    type: 'token-info-widget',
    title: 'Token Info',
    content: <TokenInfo 
      key={uniqueKey}
      {...props} 
      isWidget={true} 
    />,
    size: { width: 450, height: 1000 },
    contentStyle: { 
      padding: 0,
      overflow: 'hidden' 
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: 'rgba(12, 20, 26, 0.7)',
      backdropFilter: 'blur(6px)',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    }
  };
}